import { ComponentClass } from '@philips-emr/tws-core/lib/registry/component-info';

import FragmentNode from './core/fragment-node';
import { FragmentMetadata, Node } from './metadata';
import MetadataStore from './metadata/metadata-store';
import Renderer from './renderer/renderer';

interface EngineArgs<C extends ComponentClass> {
  renderer: Renderer<C, unknown>;
  metadataStore: MetadataStore;
}

export interface FragmentProviderOptions {
  fragment: string;
  instanceUUID: string;
  parent?: FragmentNode | null;
  submittable?: boolean;
}

type Component = ComponentClass;

/**
 * Engine for TWS applications.
 * Responsible to hold fragment-node instances, and render page content.
 */
class Engine {

  private fragments = new Map<string, FragmentNode>();

  private renderer: Renderer<Component, unknown>;
  private metadataStore: MetadataStore;

  constructor(args: EngineArgs<Component>) {
    this.renderer = args.renderer;
    this.metadataStore = args.metadataStore;
  }

  /**
   * Discard the fragment-node instance associated to the instanceUUID.
   * The instance will be destroyed, and its resources as well.
   * 
   * @param instanceUUID the fragment-node instance identifier.
   */
  discardFragment(instanceUUID: string): void {
    const fragment = this.fragments.get(instanceUUID);

    if (fragment) {
      fragment.destroy();
      this.fragments.delete(instanceUUID);
    }
  }

  /**
   * Retrieves the fragment metadata from MetadataStore.
   * 
   * @param name the fragment name.
   * @returns the fragment metadata instance or null.
   */
  getFragmentMetadata(name: string): FragmentMetadata | null {
    return this.metadataStore.get(name) || null;
  }

  /**
   * Gets or creates a fragment-node instance, based on the fragment name and instance identifier.
   * 
   * The engine will hold the fragment-node instance, that can be discarded throught Engine#discardFragment.
   * It's not allowed to have more than one instance for the same instanceUUID.
   * 
   * Note: It does not validate if the fragment exists into the MetadataStore.
   * 
   * @param {FragmentProviderOptions} options - options for the fragment that must be provided.
   * 
   * @returns the fragment instance.
   */
  provideFragment(options: FragmentProviderOptions): FragmentNode {
    const { fragment, instanceUUID, parent, submittable } = options;

    let instance = this.fragments.get(instanceUUID);

    if (!instance) {
      instance = new FragmentNode({
        engine: this,
        instanceUUID,
        name: fragment,
        parent: parent || null,
        submittable,
      });

      this.fragments.set(instanceUUID, instance);
    }
    return instance;
  }

  /**
   * Registers a metadata into MetadataStore instance.
   * The name is the key to identify the fragment when executing Engine#getFragmentMetadata and Engine#provideFragment.
   * 
   * @param name the fragment name.
   * @param metadata 
   * @throws when already exists a fragment binded with the provided name.
   */
  registerMetadata(name: string, metadata: Node): void {
    this.metadataStore.register(name, metadata);
  }

  /**
   * Renders the node using the engine's held instance or else the optional argument.
   * 
   * @param node the node that must be rendered.
   * @param renderer an optional renderer that has priority over the engine's one.
   * 
   * @returns the rendered artifact.
   */
  render(node: Node, renderer?: Renderer<Component, unknown>): unknown {
    const theRenderer = renderer || this.renderer;
    return theRenderer.fromMetadata(node);
  }

}

export default Engine;
